import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";

import {
  getCapabilitiesForVersion,
  type NativeCapabilities,
  NO_NATIVE_CAPABILITIES,
} from "./nativeCapabilities.config";
import { getNativeVersionName } from "./liveUpdate.adapter";

let cachedCapabilities: NativeCapabilities | undefined;
const listeners = new Set<() => void>();

function setCapabilities(capabilities: NativeCapabilities): void {
  cachedCapabilities = capabilities;
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Subscribes to capability resolution; the listener fires when
 * `initNativeCapabilities()` resolves. Returns an unsubscribe function.
 *
 * Components should not call this directly — use `useNativeCapabilities()`.
 */
export function subscribeToNativeCapabilities(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Resolves the installed native binary's capabilities once at app startup
 * (multi-version OTA: one bundle serves many native versions; each device looks up
 * what its own binary supports).
 *
 * Fire-and-forget from `src/index.tsx` — never blocks first render. Until it
 * resolves, `nativeCapabilities()` returns all-false (the safe direction); the
 * resolved event in Datadog makes that window observable.
 */
export async function initNativeCapabilities(): Promise<void> {
  if (!Platform.isMobile) {
    setCapabilities(NO_NATIVE_CAPABILITIES);
    return;
  }

  try {
    const versionName = await getNativeVersionName();
    setCapabilities(getCapabilitiesForVersion(versionName ?? ""));
    logEvent(APP_V2_APP_EVENTS.LIVE_UPDATE_CAPABILITIES_RESOLVED, {
      versionName,
      capabilities: cachedCapabilities,
    });
  } catch (error: unknown) {
    logError(APP_V2_APP_EVENTS.LIVE_UPDATE_CAPABILITIES_RESOLUTION_FAILURE, { error });
  }
}

/**
 * Synchronous accessor for the installed binary's native capabilities.
 * All-false on web and until `initNativeCapabilities()` resolves.
 *
 * Use inside adapters to gate method-level native features at CALL time:
 * `if (!nativeCapabilities().__SUPPORTS_X__) { return; }`
 *
 * Do NOT read this during render to decide what to show — it never triggers a
 * re-render, and at first render resolution hasn't completed yet, so the
 * component would stay on the all-false branch indefinitely. Use
 * `useNativeCapabilities()` for render-time gating.
 */
export function nativeCapabilities(): NativeCapabilities {
  return cachedCapabilities ?? NO_NATIVE_CAPABILITIES;
}

/**
 * Test-only: reset the cached capabilities and subscribers between tests.
 */
export function resetNativeCapabilitiesForTesting(): void {
  cachedCapabilities = undefined;
  listeners.clear();
}
